import { motion } from 'framer-motion';
import styled, { css, keyframes } from 'styled-components';

import YatImageToggle from '@app/pages/Prototype/Grid/GridItem/Link/ImageToggle';

import { Typography } from '../../../../../components/DesignSystem';
import { RemoveButton } from '../RemoveButton';

import IconArrow from './images/arrow.svg';
import ImageGlow from './images/glow.png';

const BORDER_RADIUS_DESKTOP = 10;

export const StyledYatRemoveButton = styled(RemoveButton)`
    opacity: 1 !important;
`;

export const StyledYatImageToggle = styled(YatImageToggle)`
    opacity: 1;
`;

export const GridItemUrlWrapper = styled(motion.div)`
    position: relative;
`;

export const Image = styled.div`
    flex-grow: 1;

    pointer-events: none;
    img {
        border-radius: ${BORDER_RADIUS_DESKTOP}px ${BORDER_RADIUS_DESKTOP}px 0 0;
        width: 100%;

        @media (max-width: 480px) {
            border-radius: 10px 10px 0 0;
        }
    }
`;

export const GlowBackground = styled(motion.div)`
    background: url(${ImageGlow}) center no-repeat;
    background-size: contain;
    height: 100%;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: -1;
    transition: transform ${({ theme }) => theme.transitions.speeds.normal}s
        ${({ theme }) => theme.transitions.easesCss.cubic};
`;

const WRAPPER_BASE = css`
    display: flex;
    flex-direction: column;
    z-index: 2;
    border: 2px solid;
    border-image-source: linear-gradient(224.57deg, rgba(255, 255, 255, 0.15) 0.87%, rgba(255, 255, 255, 0) 37.83%);
    background-clip: padding-box;
    border-radius: 12px;
    &:before {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: -1;
        margin: -1px;
        border-radius: inherit;
        background: rgb(74, 74, 74);
        background: linear-gradient(164deg, rgba(74, 74, 74, 1) 0%, rgba(0, 0, 0, 1) 41%, rgba(0, 0, 0, 1) 100%);
    }

    &:hover + ${GlowBackground} {
        transform: scale(2.5);
    }
`;

export const CardUrlWrapper = styled.a`
    cursor: pointer;
    ${WRAPPER_BASE}
`;

export const CardEditWrapper = styled.div`
    ${WRAPPER_BASE}
`;

export const Details = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 16px 20px;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletSmall}) {
        padding: 20px 24px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.desktopSmall}) {
        padding: 32px 40px;
    }
`;

interface SkeletonPlaceholderProps {
    $loading?: boolean;
}

const SKELETON_LOAD = keyframes`
    from {
        left: -150px;
    }
    to {
        left: 100%;
    }
`;

const SKELETON_PLACEHOLDER_BASE = css<SkeletonPlaceholderProps>`
    background: rgba(255, 255, 255, 0.05);
    height: 26px;
    border-radius: 40px;
    overflow: hidden;
    position: relative;

    ${({ $loading }) =>
        $loading &&
        css`
            :before {
                content: '';
                display: block;
                position: absolute;
                left: -150px;
                top: 0;
                height: 100%;
                width: 150px;
                background: linear-gradient(to right, transparent 0%, #363636 50%, transparent 100%);
                animation: ${SKELETON_LOAD} 1.5s cubic-bezier(0.4, 0, 0.2, 1) infinite;
            }
        `}
`;

export const TitleSkeletonPlaceholder = styled.div<SkeletonPlaceholderProps>`
    margin-bottom: 8px;
    width: 40%;
    ${SKELETON_PLACEHOLDER_BASE}
`;

export const DescriptionSkeletonPlaceholder = styled.div<SkeletonPlaceholderProps>`
    width: 100%;
    margin-bottom: 20px;
    ${SKELETON_PLACEHOLDER_BASE}
`;

export const StyledTitleTypography = styled(Typography)`
    word-break: break-word;
    margin-bottom: 18px;
    line-height: 28px !important;
`;

export const StyledDescriptionTypography = styled(Typography)`
    word-break: break-word;
    color: #909092 !important;
    margin-bottom: 20px;
`;

interface UrlWrapperProps {
    $editMode?: boolean;
    $hasFocus?: boolean;
    $hasValidUrl?: boolean;
    $isURLInvalid: boolean | undefined;
}

export const UrlWrapper = styled.div<UrlWrapperProps>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    background: ${({ theme }) => theme.shades.white[10]};
    column-gap: 5px;
    max-width: calc(100% - 40px);
    height: 26px;
    border-radius: 30px;
    padding: 0 ${({ $hasValidUrl }) => ($hasValidUrl ? 30 : 10)}px 0 10px;
    width: fit-content;
    white-space: nowrap;
    border: ${({ $isURLInvalid }) => ($isURLInvalid ? `1px solid` : 'none')};
    border-color: ${({ theme }) => theme.colors.red};

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletSmall}) {
        min-width: 100%;
    }

    &:focus-within {
        width: 100%;
    }

    ${({ $hasFocus }) =>
        $hasFocus &&
        css`
            width: 100%;
            max-width: 100%;
        `}

    ${({ $editMode }) =>
        $editMode &&
        css`
            cursor: pointer;
            transition: background ${({ theme }) => theme.transitions.speeds.fast}s;

            &:hover {
                background: ${({ theme }) => theme.shades.white[20]};
            }
        `}
`;

export const StyledUrlTypography = styled(Typography)`
    width: 100%;
    overflow: hidden;

    @media (pointer: coarse) {
        mask: linear-gradient(to left, transparent, black 30px);
    }
`;

export const UrlIcon = styled(motion.div)`
    position: absolute;
    top: 2px;
    right: 5px;
    background: url(${IconArrow}) center no-repeat;
    height: 20px;
    width: 20px;
`;

export const StyledTypography = styled(Typography)`
    width: 100%;
    margin-top: 5px;
`;

export const ErrorMessage = styled.span`
    display: block;
    color: ${({ theme }) => theme.colors.red};
    width: fit-content;
    margin: 5px 0 0;
`;
